'use client';

import * as React from 'react';
import { X } from 'lucide-react';
import { useTranslations } from 'next-intl';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface ChipsInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
  size?: 'sm' | 'md';
  /** Default 20 */
  maxChips?: number;
  /** Default 100 */
  maxLength?: number;
  /** Fired when the user tries to add beyond `maxChips`. */
  onMaxChipsReached?: (max: number) => void;
  /** Fired when an entry exceeds `maxLength`. The chip is dropped. */
  onMaxLengthExceeded?: (max: number, raw: string) => void;
  /** Fired when an entry is dropped because it duplicates an existing chip. */
  onDuplicateIgnored?: (value: string) => void;
  /** Mirrors the spinner the toolbar overlays on a pending filter. */
  rightSlot?: React.ReactNode;
  ariaLabel?: string;
  id?: string;
}

const SPLIT_REGEX = /[,\n\r]+/;

function normalizeEntries(
  raw: string,
  existing: string[],
  maxLength: number,
  onMaxLengthExceeded?: (max: number, raw: string) => void,
  onDuplicateIgnored?: (value: string) => void
): string[] {
  const existingLower = new Set(existing.map((v) => v.toLowerCase()));
  const out: string[] = [];
  const seenInBatch = new Set<string>();

  for (const part of raw.split(SPLIT_REGEX)) {
    const trimmed = part.trim();
    if (!trimmed) continue;

    if (trimmed.length > maxLength) {
      onMaxLengthExceeded?.(maxLength, trimmed);
      continue;
    }

    const key = trimmed.toLowerCase();
    if (existingLower.has(key)) {
      onDuplicateIgnored?.(trimmed);
      continue;
    }
    if (seenInBatch.has(key)) continue;

    seenInBatch.add(key);
    out.push(trimmed);
  }

  return out;
}

function ChipsInput({
  value,
  onChange,
  placeholder,
  disabled,
  className,
  inputClassName,
  size = 'md',
  maxChips = 20,
  maxLength = 100,
  onMaxChipsReached,
  onMaxLengthExceeded,
  onDuplicateIgnored,
  rightSlot,
  ariaLabel,
  id
}: ChipsInputProps) {
  const tc = useTranslations('common');
  const [draft, setDraft] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const atCap = value.length >= maxChips;

  const commit = React.useCallback(
    (raw: string) => {
      if (!raw.trim()) return;

      const newChips = normalizeEntries(
        raw,
        value,
        maxLength,
        onMaxLengthExceeded,
        onDuplicateIgnored
      );
      if (newChips.length === 0) return;

      const remaining = Math.max(maxChips - value.length, 0);
      if (remaining === 0) {
        onMaxChipsReached?.(maxChips);
        return;
      }

      const accepted = newChips.slice(0, remaining);
      if (accepted.length < newChips.length) {
        onMaxChipsReached?.(maxChips);
      }
      onChange([...value, ...accepted]);
    },
    [
      value,
      onChange,
      maxChips,
      maxLength,
      onMaxChipsReached,
      onMaxLengthExceeded,
      onDuplicateIgnored
    ]
  );

  const removeAt = React.useCallback(
    (index: number) => {
      if (disabled) return;
      onChange(value.filter((_, i) => i !== index));
    },
    [disabled, onChange, value]
  );

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commit(draft);
      setDraft('');
      return;
    }
    if (event.key === ',') {
      event.preventDefault();
      commit(draft);
      setDraft('');
      return;
    }
    if (
      event.key === 'Backspace' &&
      draft.length === 0 &&
      value.length > 0 &&
      !disabled
    ) {
      event.preventDefault();
      removeAt(value.length - 1);
    }
  };

  const onPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData.getData('text');
    if (!SPLIT_REGEX.test(text)) return;

    event.preventDefault();
    commit(`${draft}${text}`);
    setDraft('');
  };

  const onBlur = () => {
    if (draft.trim()) {
      commit(draft);
      setDraft('');
    }
  };

  const minHeight = size === 'sm' ? 'min-h-8' : 'min-h-9';

  return (
    <div
      className={cn(
        'border-input dark:bg-input/30 group relative flex w-full flex-wrap items-center gap-1 rounded-md border bg-transparent px-2 py-1 text-sm shadow-xs transition-[color,box-shadow]',
        'focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]',
        'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
        disabled && 'pointer-events-none cursor-not-allowed opacity-50',
        minHeight,
        rightSlot && 'pe-8',
        className
      )}
      onClick={() => inputRef.current?.focus()}
      role='group'
      aria-label={ariaLabel}
    >
      {value.map((chip, index) => (
        <Badge
          key={`${chip}-${index}`}
          variant='secondary'
          className='gap-1 ps-2 pe-1 text-sm font-normal'
        >
          <span className='truncate'>{chip}</span>
          <button
            type='button'
            disabled={disabled}
            onClick={(event) => {
              event.stopPropagation();
              removeAt(index);
            }}
            className='hover:bg-muted-foreground/20 focus-visible:ring-ring inline-flex size-4 items-center justify-center rounded-sm focus-visible:ring-1 focus-visible:outline-none'
            aria-label={tc('chipRemove', { value: chip })}
          >
            <X className='size-3' />
          </button>
        </Badge>
      ))}
      <input
        ref={inputRef}
        id={id}
        type='text'
        value={draft}
        disabled={disabled}
        placeholder={
          value.length === 0
            ? (placeholder ?? tc('chipPlaceholderEmpty'))
            : tc('chipPlaceholderHasItems')
        }
        onChange={(event) => {
          if (atCap) {
            onMaxChipsReached?.(maxChips);
            return;
          }
          setDraft(event.target.value);
        }}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        onBlur={onBlur}
        maxLength={maxLength}
        className={cn(
          'placeholder:text-muted-foreground flex-1 bg-transparent px-1 py-0.5 text-sm outline-none',
          atCap && 'pointer-events-none opacity-50',
          inputClassName
        )}
      />
      {rightSlot && (
        <span className='pointer-events-none absolute inset-y-0 end-2 flex items-center'>
          {rightSlot}
        </span>
      )}
    </div>
  );
}

export { ChipsInput };
export type { ChipsInputProps };
